<template>
  <v-container fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="10" md="8" lg="6" xl="4">
        <h1 class="text-h6 font-weight-light text-uppercase text-center my-12">
          Create Account
        </h1>
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          @submit.prevent="noop"
        >
          <v-row>
            <v-col cols="12" md="6" class="py-0">
              <v-text-field
                v-model="name"
                :rules="nameRules"
                label="Enter your name"
                outlined
                validate-on-blur
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="py-0">
              <v-text-field
                v-model="lastName"
                :rules="lastNameRules"
                label="Enter your last name"
                outlined
                validate-on-blur
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" class="py-0">
              <v-text-field
                v-model.trim="email"
                :rules="emailRules"
                label="Enter your e-mail address"
                outlined
                validate-on-blur
                required
                @keydown.space.prevent
              ></v-text-field>
              <v-text-field
                v-model="password"
                :rules="passwordRules"
                :append-icon="
                  showPassword ? 'fal fa-eye fa-sm' : 'fal fa-eye-slash fa-sm'
                "
                :type="showPassword ? 'text' : 'password'"
                label="Enter your password"
                min="8"
                outlined
                validate-on-blur
                required
                @click:append="() => (showPassword = !showPassword)"
              ></v-text-field>
              <v-text-field
                v-model="repeatPassword"
                :rules="repeatPasswordRules"
                :append-icon="
                  showPassword ? 'fal fa-eye fa-sm' : 'fal fa-eye-slash fa-sm'
                "
                :type="showPassword ? 'text' : 'password'"
                label="Repeat your password"
                outlined
                validate-on-blur
                required
                @click:append="() => (showPassword = !showPassword)"
              ></v-text-field>
              <div class="mb-6 d-flex justify-center">
                <recaptcha />
              </div>
              <p class="text-body-2 mb-6">
                By clicking Submit or signing up you agree to the
                <nuxt-link to="/terms-and-conditions"
                  >Terms and Conditions</nuxt-link
                >
                and <nuxt-link to="/privacy-policy">Privacy Policy</nuxt-link>.
              </p>
              <v-btn
                :loading="processing"
                depressed
                type="submit"
                dark
                x-large
                block
                class="text-body-2 text-uppercase ma-0 h-60"
                @click="submit"
                >Submit</v-btn
              >
            </v-col>
          </v-row>
        </v-form>
        <v-row class="mt-6">
          <v-col class="my-2" cols="12">
            <p class="mb-0 text-center text-uppercase text-body-2">or</p>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn
              :loading="processing"
              depressed
              block
              x-large
              color="google"
              dark
              class="text-body-2 text-uppercase ma-0 h-60"
              @click="oauthSignup('google')"
            >
              <v-icon>fab fa-google</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn
              :loading="processing"
              depressed
              block
              x-large
              color="facebook"
              dark
              class="text-body-2 text-uppercase ma-0 h-60"
              @click="oauthSignup('facebook')"
            >
              <v-icon>fab fa-facebook-f</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn
              :loading="processing"
              depressed
              block
              x-large
              color="black white--text"
              dark
              class="text-body-2 text-uppercase ma-0 h-60"
              @click="oauthSignup('apple')"
            >
              <v-icon>fab fa-apple</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <p class="text-body-2 text-uppercase text-center mt-12">
          Already a member?
          <nuxt-link class="inline--text" to="/login">Login</nuxt-link>
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { mapState } from 'vuex'
import { SignInWithApple } from '@capacitor-community/apple-sign-in'
import { EventBus } from '@/plugins/event-bus'
import { displayError } from '@/utils/swal'

export default {
  middleware: 'notAuthenticated',
  data() {
    return {
      valid: false,
      processing: false,
      name: '',
      nameRules: [
        v => !!v || 'Please enter your name',
        v =>
          !v ||
          (v && v.length >= 2) ||
          'Your name should be at least 2 characters length',
        v =>
          (v && v.length <= 30) ||
          'Your name should be max 30 characters length'
      ],
      lastName: '',
      lastNameRules: [
        v => !!v || 'Please enter your last name',
        v =>
          !v ||
          (v && v.length >= 2) ||
          'Your lastname should be at least 2 characters length',
        v =>
          (v && v.length <= 30) ||
          'Your last name should be max 30 characters length'
      ],
      password: '',
      passwordRules: [
        v => !!v || 'Please enter your password',
        v =>
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9!@#$%^&*])(?=.{8,})/.test(v) ||
          'Your passwords must be at least 8 characters length and contain uppercase and lowercase characters and at least one number'
      ],
      repeatPassword: '',
      repeatPasswordRules: [
        v => !!v || 'Please repeat your password',
        v => v === this.password || 'Your passwords should match'
      ],
      email: '',
      emailRules: [
        v => !!v || 'Please enter your email address',
        v =>
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            v
          ) || 'Please enter a valid email address'
      ],
      showPassword: false
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'Create your 100ASA Account',
      description: 'Sign up to the best community of photographers on the web. Create your account in a few seconds. Choose your password.'
    })
  },
  computed: {
    ...mapState({
      referral: state => state.adverts.advertSource
    })
  },
  mounted() {
    // this.$ga.event({
    //   eventCategory: 'tracking',
    //   eventAction: 'page load',
    //   eventLabel: 'signup screen',
    //   eventValue: true
    // })
  },
  methods: {
    async submit() {
      if (this.$refs.form.validate()) {
        this.processing = true
        const response = await this.$axios.$get(
          `${process.env.baseUrl}/proxy/check-disposable?email=${this.email}`
        )
        const data = JSON.parse(response)
        if (data.disposable === 'true') {
          // this.$ga.event({
          //   eventCategory: 'tracking',
          //   eventAction: 'signup',
          //   eventLabel: 'disposable email',
          //   eventValue: true
          // })
          EventBus.$emit('snackbar', {
            text: 'Your email provider is not supported',
            color: 'info'
          })
          this.processing = false
          return
        }
        try {
          const token = await this.$recaptcha.getResponse()
          const credentials = {
            name: this.name.replace(/^\/\/|^.*?:(\/\/)?/, ''),
            lastName: this.lastName.replace(/^\/\/|^.*?:(\/\/)?/, ''),
            email: this.email,
            password: this.password,
            token
          }
          await this.$store.dispatch('authentication/signup', credentials)
          this.clear()
          this.$router.push('/welcome?local=yes')
        } catch (error) {
          this.$asa.appInsights('Pages: signup.vue - method: submit', error)
        } finally {
          await this.$recaptcha.reset()
          this.processing = false
        }
      }
    },
    async oauthSignup(provider) {
      try {
        this.processing = true
        await this.$store.dispatch('authentication/cleanCookies')
        if (provider === 'apple') {
          const options = {
            clientId: 'com.100asa.www',
            redirectURI: 'https://100asa.com/callback',
            scopes: 'email name',
          }
          const { response } = await SignInWithApple.authorize(options)
          if (response && !response.email) {
            displayError('Email not found, please try again')
          }
          if (response && response.identityToken) {
            const data = {
              token: response.identityToken,
              email: response.email,
              givenName: response.givenName,
              familyName: response.familyName,
              provider
            }
            await this.$store.dispatch('authentication/oauth', data)
          } else {
            displayError('Error social credentials')
          }
        } else {
          await this.$auth.loginWith(provider)
        }
      } catch (error) {
        this.$asa.appInsights('Pages: signup.vue - method: oauthSignup', error)
      } finally {
        this.processing = false
      }
    },
    clear() {
      this.$refs.form.reset()
    },
    noop() {}
  }
}
</script>
